import useI18nTl from '@/hooks/useI18nTl'
type ValidatorFunction = (rule: any, value: any, callback: (error?: Error) => void) => void
class FormRulesFactory {
  message: string // 空值的文案
  validator?: ValidatorFunction
  pattern?: RegExp
  trigger?: string
  required?: boolean
  // len?: number
  min?: number
  max?: number
  // type?: string
  constructor(
    message: string,
    options: {
      validator?: ValidatorFunction
      pattern?: RegExp
      trigger?: string
      required?: boolean
      min?: number
      max?: number
      type?: string
      len?: number
      minLen?: number
      maxLen?: number
      maxDecimalPlaces?: number
    } = {},
  ) {
    const {
      validator,
      pattern,
      trigger = 'blur', // 默认值
      required = true, // 默认值
      min,
      max,
      type,
      len,
      minLen,
      maxLen,
      maxDecimalPlaces,
    } = options
    const { tl } = useI18nTl('formRules')
    this.message = tl(message)
    this.trigger = trigger

    if (pattern) {
      this.pattern = pattern
    }
    if (required) this.required = required
    if (validator) this.validator = validator
    if (min && max && type && type === 'number') {
      this.message = tl('inputRangeInvalid', { min, max })
    } else if (min && max) {
      this.message = tl('stringLengthInvalid', { min, max })
    }
    if (len) this.message = tl('stringLengthExact', { len })
    if (minLen) {
      this.min = minLen
      this.message = tl('minStringLength', { minLen })
    }
    if (maxLen) {
      this.max = maxLen
      this.message = tl('maxStringLength', { maxLen })
    }
    if (maxDecimalPlaces) {
      this.message = tl('maxDecimalPlaces', { maxDecimalPlaces })
    }
    // if (len) this.len = len
    // if (type) this.type = type
  }

  // 批量生成自定义的规则
  static generateCustomRules(
    fields: string[],
    message: string, // 空值国际化文案的key值
    validatorMessage: string, // 验证规则不通过的国际化文案key值
    options: {
      validatorName?: string // 验证器函数名
      pattern?: RegExp // 可选参数
      type?: string
      min?: number
      max?: number
      len?: number
      minLen?: number
      maxLen?: number
      required?: boolean
      maxDecimalPlaces?: number
      removeZeroCheck?: boolean
    } = {},
  ): { [key: string]: FormRulesFactory[] } {
    const rules: { [key: string]: FormRulesFactory[] } = {}
    const {
      validatorName,
      pattern,
      type,
      min,
      max,
      len,
      minLen,
      maxLen,
      required,
      maxDecimalPlaces,
      removeZeroCheck,
    } = options
    // 获取传入的验证器函数
    const validator = validatorName ? FormRulesFactory.getValidatorByName(validatorName) : undefined

    // 为每个字段生成规则
    fields.forEach((field) => {
      // rules[field] = [
      //   new FormRulesFactory(validatorMessage, {
      //     validator: FormRulesFactory.checkZeroValue,
      //     required: false,
      //   }),
      // ]
      rules[field] = []
      if (removeZeroCheck !== true) {
        rules[field] = [
          new FormRulesFactory(validatorMessage, {
            validator: FormRulesFactory.checkZeroValue,
            required: false,
          }),
        ]
      }
      // 必填项目
      if (required !== false) {
        rules[field].push(
          new FormRulesFactory(message, {
            validator: FormRulesFactory.checkEmptyValue,
          }),
        )
        rules[field].push(
          new FormRulesFactory(message, {
            validator: FormRulesFactory.checkEmptyValue,
            trigger: 'change',
          }),
        )
      }
      if (validatorName) {
        rules[field].push(
          new FormRulesFactory(validatorMessage, {
            validator: validator,
            required: false,
          }),
        )
      }
      if (min && max && type === 'number') {
        const rangeFunc = new RuleFunction(min, max)
        rules[field].push(
          new FormRulesFactory(validatorMessage, {
            validator: rangeFunc.checkNumberRange,
            min: min,
            max: max,
            type: type,
            required: false,
          }),
        )
      }
      if (min && max && type !== 'number') {
        const rangeFunc = new RuleFunction(min, max)
        rules[field].push(
          new FormRulesFactory(validatorMessage, {
            validator: rangeFunc.checkLengthRange,
            min: min,
            max: max,
            type: type,
            required: false,
          }),
        )
      }
      if (len) {
        const lenFunc = new RuleFunction(0, 0, len)
        rules[field].push(
          new FormRulesFactory(validatorMessage, {
            validator: lenFunc.checkFixedLength,
            len: len,
            required: false,
          }),
        )
      }
      if (pattern && maxDecimalPlaces) {
        rules[field].push(
          new FormRulesFactory(validatorMessage, {
            pattern: pattern,
            required: false,
            maxDecimalPlaces: maxDecimalPlaces,
          }),
        )
      }
      if (pattern && !maxDecimalPlaces) {
        rules[field].push(
          new FormRulesFactory(validatorMessage, {
            pattern: pattern,
            required: false,
          }),
        )
      }
      if (minLen) {
        rules[field].push(
          new FormRulesFactory(validatorMessage, {
            minLen: minLen,
            required: false,
          }),
        )
      }
      if (maxLen) {
        rules[field].push(
          new FormRulesFactory(validatorMessage, {
            maxLen: maxLen,
            required: false,
          }),
        )
      }
    })
    return rules
  }

  // 动态获取验证器函数
  static getValidatorByName(validatorName: string): ValidatorFunction | undefined {
    const validator = (FormRulesFactory as any)[validatorName]
    if (typeof validator === 'function') {
      return validator
    }
    return undefined
  }

  // 默认的空值规则
  static checkEmptyValue: ValidatorFunction = (rule: any, value: any, callback: any) => {
    if (value === null || value === undefined || value === '') {
      callback(new Error())
    } else {
      callback()
    }
  }
  // 默认的零值规则
  static checkZeroValue: ValidatorFunction = (rule: any, value: any, callback: any) => {
    if (value === 0 || value === '0') {
      callback(new Error())
    } else {
      callback()
    }
  }
  // 默认的空对象
  static checkEmptyObject: ValidatorFunction = (rule: any, value: any, callback: any) => {
    if (typeof value === 'object' && value !== null && Object.keys(value).length === 0) {
      callback(new Error())
    } else {
      callback()
    }
  }
  // 默认的空数组
  static checkEmptyArrValue: ValidatorFunction = (rule: any, value: any, callback: any) => {
    if (typeof value === 'object' && value !== null && Array.isArray(value) && value.length === 0) {
      callback(new Error())
    } else {
      callback()
    }
  }
}

class RuleFunction {
  min: number
  max: number
  len?: number
  constructor(min: number, max: number, len?: number) {
    this.min = min
    this.max = max
    if (len) this.len = len
  }
  // 用于检测值是否在范围内的验证函数
  checkNumberRange: ValidatorFunction = (rule: any, value: any, callback: any) => {
    const extractNumber = (input: string | number): number | null => {
      if (typeof input === 'number') {
        return input
      }
      if (typeof input === 'string') {
        const match = input.match(/[+-]?\d+/)
        return match ? parseFloat(match[0]) : null
      }
      return null
    }

    const extractedNumber = extractNumber(value)
    if (extractedNumber === null) {
      callback(new Error())
      return
    }

    // 检测是否在范围内
    if (extractedNumber < this.min || extractedNumber > this.max) {
      callback(new Error())
    } else {
      callback()
    }
  }
  // 用于检测值长度是否在范围内的验证函数
  checkLengthRange: ValidatorFunction = (rule: any, value: any, callback: any) => {
    let length: number
    // 判断value类型并计算其长度
    if (typeof value === 'string' || Array.isArray(value)) {
      length = value.length // 字符串或数组
    } else if (typeof value === 'number') {
      length = value.toString().length // 数字类型，转为字符串后计算长度
    } else {
      callback(new Error()) // 仅支持字符串、数组或数字
      return
    }

    // 判断长度是否在 min 和 max 之间
    if (length < this.min || length > this.max) {
      callback(new Error())
    } else {
      callback()
    }
  }
  // 用于检测值长度
  checkFixedLength: ValidatorFunction = (rule: any, value: any, callback: any) => {
    let length: number
    // 判断value类型并计算其长度
    if (typeof value === 'string' || Array.isArray(value)) {
      length = value.length // 字符串或数组
    } else if (typeof value === 'number') {
      length = value.toString().length // 数字类型，转为字符串后计算长度
    } else {
      callback(new Error()) // 仅支持字符串、数组或数字
      return
    }

    // 判断长度
    if (length !== this.len) {
      callback(new Error())
    } else {
      callback()
    }
  }
}
export default FormRulesFactory
